<template>
  <div class="page">
    <h1>FCBS Carbon</h1>
    <p>FCBS CARBON is a free whole life carbon review tool. It is designed to estimate the whole life carbon of a building
      at early design stages, to inform design decisions prior to detailed design.</p>
    <P>Using benchmarked data from the ICE Database and EPDs, FCBS CARBON is designed to give the design team insight into
      the whole life carbon impact of a building from the very outset of a project. Easy to use and with graphical
      outputs, it makes potential carbon impacts clear to the client, architect and the whole design team and puts the
      carbon cost of the project into clear perspective. </P>
    <br>
    <br>
    <div class="embed-container">
      <iframe class="videoPlayer"
        data-src="https://player.vimeo.com/video/481678356?badge=0&amp;amp;byline=0&amp;amp;color=54C9F2&amp;amp;portrait=0&amp;amp;title=0"
        frameborder="0" webkitallowfullscreen="" mozallowfullscreen="" allowfullscreen=""
        src="https://player.vimeo.com/video/481678356?badge=0&amp;amp;byline=0&amp;amp;color=54C9F2&amp;amp;portrait=0&amp;amp;title=0" />
    </div>
    <div class="panel">
      <div class="panel-header">
        <h3>To download, please provide us with some details</h3>
      </div>
      <div class="panel-content">
        <p><i>Access to FCBS CARBON is provided for free, but on the basis that we may contact you for feedback to improve
            the software. Please fill in the fields below, and then you'll be given a personalised download link.</i></p>
        <div class="panel-property panel-property--full-width">
          <div class="panel-label">
            <label>
              Your full name
            </label>
          </div>
          <input :class="GetValidatedClass('fullName')" placeholder="Your name" type="text" v-model="details.fullName"
            @change="() => { ValidateProperty('fullName', ValidateEmptyString) }" />
          <p class="panel-error">{{ this.validationErrors['fullName'] }}</p>
        </div>
        <div class="panel-property panel-property--full-width">
          <div class="panel-label">
            <label>
              Your email address
            </label>
          </div>
          <input placeholder="john.doe@email.com" :class="GetValidatedClass('email')" type="email" v-model="details.email"
            @change="() => { ValidateProperty('email', ValidateEmail) }" />
          <p class="panel-error">{{ this.validationErrors['email'] }}</p>
        </div>
        <div class="panel-property panel-property--full-width">
          <div class="panel-label">
            <label>
              Company name (if applicable)
            </label>
          </div>
          <input placeholder="Company name" type="text" v-model="details.companyName" />
        </div>
        <div class="panel-property panel-property--full-width">
          <div class="panel-label">
            <label>
              Business sector
            </label>
          </div>
          <select :class="GetValidatedClass('sector')" v-model="details.sector"  @change="() => { ValidateProperty('sector', ValidatePositiveInteger) }">
            <option v-for="(sector, index) in sectors" :key="index" :value="sector.value">
              {{ sector.text }}
            </option>
          </select>
          <p class="panel-error">{{ this.validationErrors['sector'] }}</p>
        </div>
        <button v-if="IsValidated()" @click="SaveDetails">Submit</button>
      </div>
    </div>
    <br>
    <p>
      FCBS CARBON is distributed as a beta version to provide guidance at early stages of building design. It is intended
      to provide a consistent methodology to understand the magnitude of design decisions, enabling whole life carbon to
      be compared between different options. The tool is provided as a macro enabled spreadsheet, please ensure you enable
      the macros to experience the full functionality of FCBS CARBON.
    </p>
    <br>
    <p>
      The tool provides an estimate of the whole life carbon, and users must ensure that the modelled parameters of any
      building represent it as accurately as possible for that design stage. All claims relating to whole life carbon
      arising as result of this tool should be verified by a suitably qualified professional. FCBStudios provide no
      warranty as to the accuracy of the results from this tool.
    </p>
    <br>
    <p>
      The tool has been developed by FCBStudios Ltd using carbon factors derived from the Inventory of Carbon and Energy
      (ICE) database and Environmental Product Declarations (EPDs).
    </p>
    <br>
    <p>Please credit any work using FCBS CARBON to FCBStudios.
    </p>
    <br>
    <p>
      By downloading the tool you consent to FCBStudios to contact you regarding your experience of using the tool to
      guide future development including an online version. Data provided during this exercise will be provided
      anonymously unless explicit permission has been granted by the user.
    </p>
    <br>
    <p>
      The tool is issued under the Creative Commons CC BY-NC-ND-4.0 International licence.
      (https://creativecommons.org/licenses/by-nc-nd/4.0/ )
    </p>
    <br>
    <p>
      FCBStudios wish to thank the following individuals for their guidance in the preparation of the tool for free
      release to the industry:
    </p>
    <br>
    <p>
      For discussion on structural assumptions
    </p>
    <ul>
      <li>Marta Galinanes Garcia and Edoardo Tibuzzi at AKT II</li>
      <li>Steve Webb and Alex Lynes, at Webb Yates</li>
      <li>Julia Ratcliffe at Scale Consulting</li>
    </ul>
    <br>
    <p>
      For discussion on the benchmarking of whole life carbon methodologies
    </p>
    <ul>
      <li>Simon Sturgis <a href="https://www.targetingzero.co.uk/" target="_blank">https://www.targetingzero.co.uk/</a>
      </li>
    </ul>
    <br>
    <a href="https://creativecommons.org/licenses/by-nc-nd/4.0/">
      <img src="@/assets/cc.png" />
    </a>
    <br>
    <br>
    <p>
      This work is licensed under <a href="https://creativecommons.org/licenses/by-nc-nd/4.0/">a Creative Commons
        Attribution - NonCommercial - NoDerivatives 4.0 International License.</a>
    </p>
  </div>
</template>

<script>
import $fetch from '@/js/fetch'
import Vue from 'vue'
export default {
  data() {
    return {
      id: 0,
      sectors: [],
      details: {
        fullName: '',
        email: '',
        companyName: '',
        sector: -1,
        pass: 'afefgawefjuuun2neyyybbytyy'
      },
      validationErrors: {},
      interactedWith: {},
    }
  },
  async mounted() {
    await this.GetBusinessSectors()
  },
  methods: {
    async GetBusinessSectors() {
      this.sectors = await $fetch({ ctx: this }, `${this.$store.state.API_URL}/FCBSCarbon/GetSectorsList`)
    },
    // Validation
    /**
     * ValidateProperty
     * @param {*} property: Any
     * @param {*} validator: Function
     */
    GetValidatedClass(property) {
      let errorClass = 'panel-input--error'
      let successClass = 'panel-input--success'
      if (!this.interactedWith[property]) return ''
      else return this.validationErrors[property] ? errorClass : successClass
    },
    ValidateProperty(property, validator) {
      let data = this.details
      if (!validator) return
      let message = validator(data[property])
      Vue.set(this.interactedWith, property, true)
      if (message != null) {
        Vue.set(this.validationErrors, property, message)
      } else if (this.validationErrors[property]) Vue.set(this.validationErrors, property, null) // delete this.validationErrors[property]
    },
    ValidateEmptyString(string) {
      let validated = string.match(/\p{L}+/u)
      if (!validated || validated.length === 0) return 'Field cannot be empty.'
      return null
    },
    ValidatePositiveInteger(int) {
      let validated = int > -1
      if(!validated) return 'Field cannot be empty.'
      return null
    },
    ValidateEmail(email) {
      if (email.length === 0) return 'Field cannot be empty'
      let validated = String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
      if (!validated) return 'Valid email is required.'
      return null
    },
    ValidateTel(tel) {
      let validated = tel.match(/\d/g)
      if (!validated || (validated.length !== 10 && validated.length !== 11)) return 'Valid telephone number is required.'
      return null
    },
    IsValidated() {
      let validated = true
      if (this.validationErrors['fullName'] || !this.interactedWith['fullName']) validated = false
      if (this.validationErrors['email'] || !this.interactedWith['email']) validated = false
      if (this.validationErrors['sector'] || !this.interactedWith['sector']) validated = false
      return validated
    },
    //
    async SaveDetails() {
      this.details.sector = parseFloat(this.details.sector)
      const result = await $fetch(
        {
          ctx: this,
          method: 'POST',
          body: this.details
        },
        `${this.$store.state.API_URL}/FCBSCarbon/SaveDetails`)
      if(result.contractSuccessful) {
        this.id = result.returnModel
        await this.DownloadCarbonTool()
      }
    },
    async DownloadCarbonTool() {
      window.open(`${this.$store.state.API_URL}/FCBSCarbon/DownloadCarbonTool?id=${this.id}`, '_blank')
    }
  }
}
</script>

<style>
.embed-container {
  --video--width: 471;
  --video--height: 295;

  position: relative;
  padding-bottom: calc(var(--video--height) / var(--video--width) * 100%);
  /* 41.66666667% */
  overflow: hidden;
  max-width: 100%;
  background: whitesmoke;
}

.embed-container iframe,
.embed-container object,
.embed-container embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}</style>